import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { fetchApiLogs } from '../../services/cloudService';
import type { ApiLogZeile } from '../../services/cloudService';
import { useRememberedState } from '../../hooks/useRemembered';
import { Cloud, Loader2, Users, Cpu, FileText, CalendarRange, RefreshCw, XCircle } from 'lucide-react';
import {
    API_LOG_STANDARD,
    API_LOG_VOREINSTELLUNGEN,
    gueltigeVoreinstellung,
    ladefehlerText,
    zeitraumGrenzen,
    zeitraumText,
} from './adminZeitraum';
import type { ApiLogVoreinstellung, ZeitraumGrenzen } from './adminZeitraum';

const ACTION_LABELS: Record<string, string> = {
    analyzeInspectionPhoto: 'Mängelfoto-Analyse (KI)',
    processVoiceTranscript: 'Sprachprotokoll-Transkription',
    generateSafetyText: 'Erstellung Mängelbericht-Text',
    fetchSafetyNews: 'Arbeitsschutz-Newsfeed Suche',
    getFieldExplanation: 'Begriffs-Erklärung (SiGeKo)',
    extractContacts: 'Kontakt-Datenextraktion',
    extractServices: 'LV-Positionen Extraktion',
    importOldReport: 'Import aus Altem Bericht',
    analyzeSigePlan: 'SiGe-Plan-Analyse',
    predictHazards: 'Gefährdungs-Vorhersage',
    generateUfsaCatalog: 'Unterlage RAB 32: Katalog-Erstellung',
    generateUfsaEntry: 'Unterlage RAB 32: Bauteil-Eintrag',
    analyzeHazardImage: 'Unterlage RAB 32: Foto-Gefahrenanalyse',
    analyzeHazardSpot: 'Unterlage RAB 32: Bildausschnitt-Analyse',
    translateReportData: 'Übersetzung Bericht (DE -> EN)',
    suggestBugFix: 'Bug-Analyse (KI)',
    regelwerkPruefung: 'Regelwerks-Prüfung (online)'
};

/**
 * So viele Einzelprotokolle kommen ins DOM (die neuesten). „Alles“ sind
 * fuenfstellig viele Zeilen — die Summen rechnen trotzdem ueber alle.
 */
const TABELLE_MAX = 500;
const KEINE_LOGS: ApiLogZeile[] = [];

type Ladestand =
    | { schluessel: string; art: 'ok'; logs: ApiLogZeile[]; grenzen: ZeitraumGrenzen }
    | { schluessel: string; art: 'fehler'; meldung: string };

/** Wofuer ein Ladestand gilt: Zeitraum + Zaehler des Aktualisieren-Knopfs. */
const ladeSchluessel = (voreinstellung: ApiLogVoreinstellung, neuladen: number): string => `${voreinstellung}#${neuladen}`;

const formatCost = (cost: number | null) => {
    const betrag = Number(cost) || 0;
    if (betrag === 0) return '0.0000 €';
    return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 4,
        maximumFractionDigits: 6
    }).format(betrag);
};

export const AdminApiLogsView: React.FC = () => {
    const [gemerkt, setVoreinstellung] = useRememberedState<ApiLogVoreinstellung>('admin.apilogs.zeitraum', API_LOG_STANDARD);
    const voreinstellung = gueltigeVoreinstellung(gemerkt, API_LOG_VOREINSTELLUNGEN, API_LOG_STANDARD);
    const [neuladen, setNeuladen] = useState(0);
    const [stand, setStand] = useState<Ladestand | null>(null);

    // Laden bei jedem Zeitraumwechsel, gefiltert in der Datenbank. Kein
    // setState im Effektrumpf (react-hooks/set-state-in-effect): „laedt“
    // ergibt sich daraus, dass der letzte Stand zu einem anderen Schluessel
    // gehoert. Ein Fehler endet als Fehlerstand — frueher blieb der Spinner
    // haengen, und leere Summen hiessen „keine Kosten“.
    useEffect(() => {
        let abgebrochen = false;
        const schluessel = ladeSchluessel(voreinstellung, neuladen);
        const grenzen = zeitraumGrenzen(voreinstellung, new Date());
        void fetchApiLogs({ ab: grenzen.ab }).then(
            (logs) => { if (!abgebrochen) setStand({ schluessel, art: 'ok', logs, grenzen }); },
            (e: unknown) => { if (!abgebrochen) setStand({ schluessel, art: 'fehler', meldung: ladefehlerText(e) }); },
        );
        return () => { abgebrochen = true; };
    }, [voreinstellung, neuladen]);

    const neuLaden = useCallback(() => setNeuladen((n) => n + 1), []);

    const geladen = stand && stand.schluessel === ladeSchluessel(voreinstellung, neuladen) ? stand : null;
    const loading = geladen === null;
    const fehler = geladen?.art === 'fehler' ? geladen.meldung : null;
    const logs = geladen?.art === 'ok' ? geladen.logs : KEINE_LOGS;
    const zeitraum = geladen?.art === 'ok' ? zeitraumText(geladen.grenzen) : '';

    // Aggregate statistics by user (Mitarbeiter)
    const userStats = useMemo(() => {
        const stats: Record<string, { name: string; email: string; cost: number; promptTokens: number; candidateTokens: number; calls: number }> = {};
        logs.forEach(l => {
            const userName = l.profiles ? `${l.profiles.first_name || ''} ${l.profiles.last_name || ''}`.trim() : 'System / Unbekannt';
            const userEmail = l.profiles?.email || 'keine E-Mail';
            const key = l.user_id || userName;
            if (!stats[key]) {
                stats[key] = { name: userName || 'System', email: userEmail, cost: 0, promptTokens: 0, candidateTokens: 0, calls: 0 };
            }
            stats[key].cost += Number(l.estimated_cost) || 0;
            stats[key].promptTokens += Number(l.prompt_tokens) || 0;
            stats[key].candidateTokens += Number(l.candidate_tokens) || 0;
            stats[key].calls += 1;
        });
        return Object.values(stats).sort((a, b) => b.cost - a.cost);
    }, [logs]);

    // Aggregate statistics by function (Aktion)
    const actionStats = useMemo(() => {
        const stats: Record<string, { label: string; key: string; cost: number; promptTokens: number; candidateTokens: number; calls: number }> = {};
        logs.forEach(l => {
            const actionKey = l.action || 'andere';
            const actionLabel = ACTION_LABELS[actionKey] || actionKey;
            if (!stats[actionKey]) {
                stats[actionKey] = { label: actionLabel, key: actionKey, cost: 0, promptTokens: 0, candidateTokens: 0, calls: 0 };
            }
            stats[actionKey].cost += Number(l.estimated_cost) || 0;
            stats[actionKey].promptTokens += Number(l.prompt_tokens) || 0;
            stats[actionKey].candidateTokens += Number(l.candidate_tokens) || 0;
            stats[actionKey].calls += 1;
        });
        return Object.values(stats).sort((a, b) => b.cost - a.cost);
    }, [logs]);

    const totals = useMemo(() => {
        let cost = 0;
        let prompt = 0;
        let candidate = 0;
        for (const l of logs) {
            cost += Number(l.estimated_cost) || 0;
            prompt += Number(l.prompt_tokens) || 0;
            candidate += Number(l.candidate_tokens) || 0;
        }
        return { cost, prompt, candidate };
    }, [logs]);

    // Die Liste kommt neueste zuerst (fetchApiLogs) — abgeschnitten wird hinten.
    const tabellenZeilen = useMemo(() => logs.slice(0, TABELLE_MAX), [logs]);
    const weitere = logs.length - tabellenZeilen.length;

    const zeitraumLeiste = (
        <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 mr-1">
                <CalendarRange className="w-4 h-4 text-slate-400" />
                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wide">Zeitraum</span>
            </div>
            {API_LOG_VOREINSTELLUNGEN.map(o => {
                const aktiv = o.wert === voreinstellung;
                return (
                    <button
                        key={o.wert}
                        type="button"
                        onClick={() => setVoreinstellung(o.wert)}
                        aria-pressed={aktiv}
                        className={`h-8 px-3 rounded-full text-[13px] font-semibold whitespace-nowrap ${aktiv ? 'bg-slate-800 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                    >
                        {o.label}
                    </button>
                );
            })}
            <div className="ml-auto flex items-center gap-3">
                {zeitraum && (
                    <span className="text-xs text-slate-500">
                        Summen: <span className="font-semibold text-slate-700">{zeitraum}</span>
                    </span>
                )}
                <button
                    type="button"
                    onClick={neuLaden}
                    disabled={loading}
                    className="p-2 bg-slate-50 border border-slate-200 rounded-lg text-slate-400 hover:text-blue-600 transition-all disabled:opacity-50"
                    title="Aktualisieren"
                >
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
            </div>
        </div>
    );

    if (loading) {
        return (
            <div className="space-y-6">
                {zeitraumLeiste}
                <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                    <Loader2 className="w-8 h-8 animate-spin mb-4" />
                    <p>Lade API Protokolle...</p>
                </div>
            </div>
        );
    }

    if (fehler) {
        return (
            <div className="space-y-6">
                {zeitraumLeiste}
                <div className="bg-red-50 rounded-2xl border border-red-200 p-8 text-center">
                    <XCircle className="w-10 h-10 mx-auto mb-3 text-red-400" />
                    <h3 className="text-sm font-bold text-red-800 mb-1">API-Protokolle konnten nicht geladen werden</h3>
                    <p className="text-xs text-red-700 font-mono break-all">{fehler}</p>
                    <p className="text-xs text-red-700 mt-2">
                        Ohne vollständige Daten werden keine Summen angezeigt — sie wären falsch.
                    </p>
                    <button
                        type="button"
                        onClick={neuLaden}
                        className="mt-4 px-4 py-2 bg-white border border-red-200 rounded-lg text-xs font-bold text-red-700 hover:bg-red-100 transition-colors"
                    >
                        Erneut versuchen
                    </button>
                </div>
            </div>
        );
    }

    if (logs.length === 0) {
        return (
            <div className="space-y-6">
                {zeitraumLeiste}
                <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center text-slate-500">
                    <Cloud className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <h3 className="text-sm font-bold text-slate-700 mb-1">Keine Logs gefunden</h3>
                    {voreinstellung !== 'alles' ? (
                        <p className="text-xs">
                            Im gewählten Zeitraum ({zeitraum}) wurden keine API Aufrufe aufgezeichnet.
                        </p>
                    ) : (
                        <>
                            <p className="text-xs">
                                Es wurden bisher keine API Aufrufe aufgezeichnet.
                            </p>
                            <div className="mt-4 p-4 bg-amber-50 rounded-lg text-amber-800 text-xs text-left max-w-lg mx-auto border border-amber-200">
                                <strong className="block mb-1">Hinweis für den Administrator:</strong>
                                Damit dieses Feature funktioniert, muss folgende Tabelle in Supabase "SQL Editor" erstellt werden:
                                <pre className="mt-2 bg-white p-2 rounded border border-amber-200 text-[11px] overflow-auto">
{`CREATE TABLE api_logs (
    id UUID DEFAULT uuid_generate_v4() PRIMARY KEY,
    created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
    action TEXT NOT NULL,
    model TEXT NOT NULL,
    prompt_tokens INTEGER DEFAULT 0,
    candidate_tokens INTEGER DEFAULT 0,
    estimated_cost NUMERIC DEFAULT 0,
    user_id UUID REFERENCES auth.users(id)
);`}
                                </pre>
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {zeitraumLeiste}

            {/* KPI Overview Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-center">
                    <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wide">Gesamtkosten (Gemini API)</span>
                    <span className="text-xl font-black text-rose-500">{formatCost(totals.cost)}</span>
                    <span className="text-[11px] text-slate-400">{zeitraum}</span>
                </div>
                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-center">
                    <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wide">Prompt Tokens (In)</span>
                    <span className="text-xl font-black text-slate-700">{totals.prompt.toLocaleString('de-DE')}</span>
                    <span className="text-[11px] text-slate-400">{zeitraum}</span>
                </div>
                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-center">
                    <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wide">Candidate Tokens (Out)</span>
                    <span className="text-xl font-black text-slate-700">{totals.candidate.toLocaleString('de-DE')}</span>
                    <span className="text-[11px] text-slate-400">{zeitraum}</span>
                </div>
                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-center">
                    <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wide">Anzahl Requests</span>
                    <span className="text-xl font-black text-blue-600">{logs.length.toLocaleString('de-DE')}</span>
                    <span className="text-[11px] text-slate-400">{zeitraum}</span>
                </div>
            </div>

            {/* Aggregates view: User & Action consumption side-by-side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* User Aggregates */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
                    <div className="p-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-blue-500" />
                            <h3 className="text-sm font-bold text-slate-700">Verbrauch nach Mitarbeiter / Team</h3>
                        </div>
                        <span className="text-[11px] bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full font-bold">
                            {userStats.length} Nutzer
                        </span>
                    </div>
                    <div className="overflow-y-auto max-h-[280px] divide-y divide-slate-100 text-xs">
                        {userStats.map((stat, idx) => (
                            <div key={idx} className="p-3 hover:bg-slate-50 transition-colors flex justify-between items-center">
                                <div className="space-y-0.5">
                                    <div className="font-semibold text-slate-800 leading-tight">{stat.name}</div>
                                    <div className="text-[11px] text-slate-400">{stat.email}</div>
                                </div>
                                <div className="text-right flex items-center gap-4">
                                    <div className="text-[11px] text-slate-400 font-mono">
                                        <div>{stat.calls} Aufrufe</div>
                                        <div>{(stat.promptTokens + stat.candidateTokens).toLocaleString('de-DE')} Tokens</div>
                                    </div>
                                    <span className="font-bold text-slate-950 font-mono bg-slate-100 px-2 py-1 rounded border border-slate-150 text-right min-w-[75px]">
                                        {formatCost(stat.cost)}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Action Aggregates */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
                    <div className="p-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Cpu className="w-4 h-4 text-violet-500" />
                            <h3 className="text-sm font-bold text-slate-700">Verbrauch nach Funktion</h3>
                        </div>
                        <span className="text-[11px] bg-violet-100 text-violet-800 px-2 py-0.5 rounded-full font-bold">
                            {actionStats.length} Funktionen
                        </span>
                    </div>
                    <div className="overflow-y-auto max-h-[280px] divide-y divide-slate-100 text-xs">
                        {actionStats.map((stat, idx) => (
                            <div key={idx} className="p-3 hover:bg-slate-50 transition-colors flex justify-between items-center">
                                <div className="space-y-0.5 pr-2">
                                    <div className="font-semibold text-slate-800 leading-tight">{stat.label}</div>
                                    <div className="text-[11px] text-slate-400 font-mono">{stat.key}</div>
                                </div>
                                <div className="text-right flex items-center gap-4 shrink-0">
                                    <div className="text-[11px] text-slate-400 font-mono">
                                        <div>{stat.calls} Aufrufe</div>
                                        <div>{(stat.promptTokens + stat.candidateTokens).toLocaleString('de-DE')} Tokens</div>
                                    </div>
                                    <span className="font-bold text-slate-950 font-mono bg-slate-100 px-2 py-1 rounded border border-slate-150 text-right min-w-[75px]">
                                        {formatCost(stat.cost)}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Detailed logs table with fixed max-height and custom scrolling */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm flex flex-col">
                <div className="p-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-slate-600" />
                        <h3 className="text-sm font-bold text-slate-700">Einzel-Protokolle (Letzte API-Aufrufe)</h3>
                    </div>
                    <span className="text-[11px] font-mono text-slate-400">{logs.length.toLocaleString('de-DE')} Einträge im Zeitraum</span>
                </div>

                <div className="overflow-x-auto overflow-y-auto max-h-[400px]">
                    <table className="w-full text-left text-xs whitespace-nowrap table-auto border-collapse">
                        <thead className="bg-slate-100 sticky top-0 z-10 text-slate-500 border-b border-slate-200 shadow-sm">
                            <tr>
                                <th className="px-4 py-2.5 font-semibold text-slate-600 bg-slate-100">Datum / Zeit</th>
                                <th className="px-4 py-2.5 font-semibold text-slate-600 bg-slate-100">Benutzer</th>
                                <th className="px-4 py-2.5 font-semibold text-slate-600 bg-slate-100">Aktion (wofür)</th>
                                <th className="px-4 py-2.5 font-semibold text-slate-600 bg-slate-100">Modell</th>
                                <th className="px-4 py-2.5 font-semibold text-slate-600 bg-slate-100 text-right">Tokens (In / Out)</th>
                                <th className="px-4 py-2.5 font-semibold text-slate-600 bg-slate-100 text-right">Geschätzte Kosten</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-150">
                            {tabellenZeilen.map((L, i) => {
                                const userName = L.profiles ? `${L.profiles.first_name || ''} ${L.profiles.last_name || ''}`.trim() || L.profiles.email : 'Unbekannt';
                                const activeActionLabel = L.action ? ACTION_LABELS[L.action] || L.action : '—';
                                return (
                                    <tr key={L.id || i} className="hover:bg-slate-50 transition-colors">
                                        <td className="px-4 py-2.5 text-slate-600">
                                            {new Date(L.created_at).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })}
                                        </td>
                                        <td className="px-4 py-2.5 font-medium text-slate-800">
                                            {userName}
                                        </td>
                                        <td className="px-4 py-2.5 text-slate-600 font-medium">
                                            <span className="bg-slate-100 text-[11px] px-2 py-0.5 rounded text-slate-700 border border-slate-150 inline-block font-sans max-w-[280px] truncate" title={L.action ?? undefined}>
                                                {activeActionLabel}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2.5 text-slate-500 font-mono text-[11px]">{L.model}</td>
                                        <td className="px-4 py-2.5 text-right text-slate-600 font-mono">
                                            <span className="text-slate-400">{L.prompt_tokens}</span> / <span className="font-semibold">{L.candidate_tokens}</span>
                                        </td>
                                        <td className="px-4 py-2.5 text-right text-rose-600 font-bold font-mono">
                                            {formatCost(L.estimated_cost)}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                {weitere > 0 && (
                    <div className="px-4 py-2.5 border-t border-slate-200 bg-slate-50 text-[11px] text-slate-500">
                        Angezeigt werden die neuesten {TABELLE_MAX.toLocaleString('de-DE')} Einträge … und {weitere.toLocaleString('de-DE')} weitere im Zeitraum (in den Summen enthalten).
                    </div>
                )}
            </div>
        </div>
    );
};
